import { useEffect, useState } from "react";
import { Users, Building2, User, Check, Eye, Loader2, Inbox } from "lucide-react";
import { api } from "../../lib/api";

const SCOPES = [
  { key: "empresa", label: "Toda la empresa", icon: Building2 },
  { key: "area", label: "Áreas", icon: Users },
  { key: "usuarios", label: "Usuarios", icon: User },
];

// Selector de audiencia usado en el formulario de creación.
export function AudiencePicker({ value, onChange }) {
  const [opts, setOpts] = useState({ areas: [], users: [] });
  const aud = value || { scope: "empresa", areas: [], user_ids: [] };
  useEffect(() => { api.get("/mod/audience/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const set = (patch) => onChange({ ...aud, ...patch });
  const toggle = (field, id) => {
    const arr = aud[field] || [];
    set({ [field]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };
  return (
    <div className="rounded-lg border border-navy/10 p-3 bg-navy/[0.02]" data-testid="audience-picker">
      <div className="text-xs text-navy/50 mb-2">Enviar a</div>
      <div className="flex gap-2 mb-3">
        {SCOPES.map((s) => (
          <button key={s.key} type="button" onClick={() => set({ scope: s.key })}
            data-testid={`audience-scope-${s.key}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs rounded-lg py-2 border transition-colors ${aud.scope === s.key ? "bg-gradient-violet text-white border-transparent" : "border-navy/15 text-navy/60 hover:border-violet"}`}>
            <s.icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        ))}
      </div>
      {aud.scope === "area" && (
        <div className="flex flex-wrap gap-2" data-testid="audience-areas">
          {opts.areas.length === 0 ? <span className="text-xs text-navy/40">Sin áreas registradas (defínelas en la ficha del empleado).</span> :
            opts.areas.map((a) => (
              <button key={a} type="button" onClick={() => toggle("areas", a)}
                className={`text-xs rounded-full px-3 py-1 border transition-colors ${(aud.areas || []).includes(a) ? "bg-violet text-white border-transparent" : "border-navy/15 text-navy/60"}`}>{a}</button>
            ))}
        </div>
      )}
      {aud.scope === "usuarios" && (
        <div className="max-h-40 overflow-y-auto space-y-1" data-testid="audience-users">
          {opts.users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
              <input type="checkbox" checked={(aud.user_ids || []).includes(u.id)} onChange={() => toggle("user_ids", u.id)} />
              <span className="text-navy/70">{u.name}</span>{u.area && <span className="text-xs text-navy/35">· {u.area}</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Marcador de lectura en vivo (para el creador) dentro del detalle.
export function ReadTracker({ moduleKey, item }) {
  const [stats, setStats] = useState(null);
  const load = () => api.get(`/mod/${moduleKey}/items/${item.id}/reads`).then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [item?.id]);
  if (!item?.audiencia) return null;
  const pct = stats && stats.total ? Math.round((stats.leidos / stats.total) * 100) : 0;
  return (
    <div className="mt-4 rounded-lg border border-navy/10 p-4" data-testid="read-tracker">
      <div className="flex items-center justify-between mb-2">
        <span className="overline text-navy/40 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Lectura en vivo</span>
        <button onClick={load} className="text-xs text-violet">Actualizar</button>
      </div>
      {!stats ? <Loader2 className="w-4 h-4 animate-spin text-navy/30" /> : (
        <>
          <div className="text-sm text-navy/70"><b data-testid="read-count">{stats.leidos}</b> de {stats.total} leyeron ({pct}%)</div>
          <div className="mt-2 h-1.5 bg-navy/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${pct}%` }} /></div>
          {stats.no_leidos?.length > 0 && (
            <div className="mt-3 text-xs text-navy/45">Faltan: {stats.no_leidos.slice(0, 12).join(", ")}{stats.no_leidos.length > 12 ? "…" : ""}</div>
          )}
        </>
      )}
    </div>
  );
}

// Bandeja de recibidos: lo que me enviaron.
export function ReceivedInbox({ moduleKey, cfg, onOpen }) {
  const [items, setItems] = useState(null);
  const first = cfg.fields[0]?.key;
  const load = () => api.get(`/mod/${moduleKey}/received`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleKey]);
  const markRead = async (it) => { await api.post(`/mod/${moduleKey}/items/${it.id}/read`); load(); };
  if (items === null) return <div className="py-10 flex justify-center text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (items.length === 0) return <div className="card-surface py-14 text-center text-navy/40 text-sm" data-testid="received-empty">No tienes elementos recibidos.</div>;
  return (
    <div className="card-surface divide-y divide-navy/5" data-testid="received-list">
      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-3 px-4 py-3" data-testid={`received-row-${it.id}`}>
          {!it._leido && <span className="w-2 h-2 rounded-full bg-violet shrink-0" title="No leído" />}
          <button onClick={() => onOpen(it)} className={`flex-1 text-left text-sm ${it._leido ? "text-navy/55" : "text-navy font-medium"}`}>{it[first] || cfg.record}</button>
          {!it._leido && <button onClick={() => markRead(it)} data-testid={`received-read-${it.id}`} className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"><Check className="w-3.5 h-3.5" /> Marcar leído</button>}
        </div>
      ))}
    </div>
  );
}

export const InboxIcon = Inbox;
